import {
  DownloadOutlined,
  LinkOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  Button,
  Input,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import apiStockOrderList from 'apps/investment/apiCalls/stockOrder/list';
import apiReconciliation from 'apps/investment/apiCalls/stockOrder/reconciliation';
import {
  orderSideColors,
  orderSideLabels,
  orderStatusColors,
  stockOrderStatusLabels,
} from 'apps/investment/constants/stockOrder';
import DatePicker from 'common/ui/UIDatePicker';
import { API_BASE_URL } from 'config/env-config';
import { useFetchApiGet } from 'hooks/api/useFetchApi';
import moment from 'moment';
import { useEffect, useState } from 'react';
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { getAccessToken, staffSessionHeaders } from 'utils/auth';

const { Title } = Typography;
const { MonthPicker } = DatePicker as any;

const MONTH_FORMAT = 'YYYY-MM';
const PAGE_SIZE = 20;

const formatVolume = (value: number) =>
  value.toLocaleString('vi-VN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const formatValue = (value: number) =>
  value.toLocaleString('vi-VN', { minimumFractionDigits: 0 });

const orderSourceColors: Record<string, string> = {
  stag: 'blue',
  nhsv: 'purple',
};

const summaryColumns = [
  {
    title: 'Tháng',
    dataIndex: 'month',
    key: 'month',
    width: 90,
  },
  {
    title: 'Mã CK',
    dataIndex: 'symbol',
    key: 'symbol',
    width: 100,
  },
  {
    title: 'Nguồn lệnh',
    dataIndex: 'order_source',
    key: 'order_source',
    width: 100,
    render: (v: string) => (
      <Tag color={orderSourceColors[v] || 'default'}>{v}</Tag>
    ),
  },
  {
    title: 'Tổng lệnh',
    dataIndex: 'total_orders',
    key: 'total_orders',
    align: 'right' as const,
    width: 90,
  },
  {
    title: 'Lệnh mua',
    dataIndex: 'total_buy_orders',
    key: 'total_buy_orders',
    align: 'right' as const,
    width: 90,
    render: (v: number) => <span style={{ color: '#389e0d' }}>{v}</span>,
  },
  {
    title: 'Lệnh bán',
    dataIndex: 'total_sell_orders',
    key: 'total_sell_orders',
    align: 'right' as const,
    width: 90,
    render: (v: number) => <span style={{ color: '#cf1322' }}>{v}</span>,
  },
  {
    title: 'KL mua khớp',
    dataIndex: 'total_buy_matched_volume',
    key: 'total_buy_matched_volume',
    align: 'right' as const,
    render: formatVolume,
  },
  {
    title: 'KL bán khớp',
    dataIndex: 'total_sell_matched_volume',
    key: 'total_sell_matched_volume',
    align: 'right' as const,
    render: formatVolume,
  },
  {
    title: 'GT mua khớp (VND)',
    dataIndex: 'total_buy_matched_value',
    key: 'total_buy_matched_value',
    align: 'right' as const,
    render: formatValue,
  },
  {
    title: 'GT bán khớp (VND)',
    dataIndex: 'total_sell_matched_value',
    key: 'total_sell_matched_value',
    align: 'right' as const,
    render: formatValue,
  },
  {
    title: 'Phí (VND)',
    dataIndex: 'total_fee',
    key: 'total_fee',
    align: 'right' as const,
    render: formatValue,
  },
  {
    title: 'Thuế (VND)',
    dataIndex: 'total_tax',
    key: 'total_tax',
    align: 'right' as const,
    render: formatValue,
  },
];

const orderDetailColumns = [
  {
    title: '',
    dataIndex: 'id',
    key: 'id',
    width: 48,
    render: (id: string) => (
      <Link to={`/investment/stock-orders/${id}`} target="_blank">
        <LinkOutlined style={{ fontSize: 18 }} />
      </Link>
    ),
  },
  {
    title: 'Số tài khoản',
    dataIndex: 'trading_account_code',
    key: 'trading_account_code',
    width: 140,
  },
  {
    title: 'Nguồn TK',
    dataIndex: 'account_source',
    key: 'account_source',
    width: 100,
    render: (v: string) =>
      v ? <Tag color={orderSourceColors[v] || 'default'}>{v}</Tag> : '-',
  },
  {
    title: 'Nhà môi giới',
    dataIndex: 'broker_name',
    key: 'broker_name',
    width: 130,
    render: (v: string) => v || '-',
  },
  {
    title: 'Order Date',
    dataIndex: 'order_date',
    key: 'order_date',
    width: 160,
    render: (v: string) => (v ? moment(v).format('DD/MM/YYYY HH:mm') : '-'),
  },
  {
    title: 'Lệnh',
    dataIndex: 'order_side',
    key: 'order_side',
    width: 100,
    render: (v: string) => (
      <Tag color={orderSideColors[v]}>{orderSideLabels[v] || v}</Tag>
    ),
  },
  {
    title: 'Trạng thái',
    dataIndex: 'status',
    key: 'status',
    width: 140,
    render: (v: number) => (
      <Tag color={orderStatusColors[v]}>{stockOrderStatusLabels[v] || v}</Tag>
    ),
  },
  {
    title: 'Mã CK',
    dataIndex: 'symbol',
    key: 'symbol',
    width: 100,
  },
  {
    title: 'Nguồn',
    dataIndex: 'order_source',
    key: 'order_source',
    width: 90,
    render: (v: string) => (
      <Tag color={orderSourceColors[v] || 'default'}>{v}</Tag>
    ),
  },
  {
    title: 'Khối lượng',
    dataIndex: 'volume',
    key: 'volume',
    align: 'right' as const,
    width: 110,
    render: (v: number) => formatVolume(v),
  },
  {
    title: 'KL khớp',
    dataIndex: 'matched_volume',
    key: 'matched_volume',
    align: 'right' as const,
    width: 110,
    render: (v: number) => formatVolume(v),
  },
  {
    title: 'Giá khớp',
    dataIndex: 'matched_price',
    key: 'matched_price',
    align: 'right' as const,
    width: 110,
    render: (v: number) => (v ? formatValue(v) : '-'),
  },
  {
    title: 'GT khớp',
    dataIndex: 'matched_value',
    key: 'matched_value',
    align: 'right' as const,
    width: 130,
    render: (v: number) => formatValue(v),
  },
  {
    title: 'NHSV Order ID',
    dataIndex: 'security_order_id',
    key: 'security_order_id',
    width: 180,
  },
  {
    title: 'Customer',
    dataIndex: 'user',
    key: 'user',
    width: 200,
    render: (user: any) =>
      user ? `${user.name || ''} (${user.email || ''})` : '-',
  },
];

const TAB_DETAIL = 'detail';
const TAB_SUMMARY = 'summary';

export default function StockOrderReconciliationPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = location.pathname.endsWith('/summary')
    ? TAB_SUMMARY
    : TAB_DETAIL;

  const [orderSource, setOrderSource] = useState(
    searchParams.get('order_source') ?? 'stag'
  );
  const [accountSource, setAccountSource] = useState(
    searchParams.get('account_source') ?? 'stag'
  );
  const [brokerName, setBrokerName] = useState(
    searchParams.get('broker_name') || ''
  );
  const [fromMonth, setFromMonth] = useState<any>(
    searchParams.get('from_month')
      ? moment(searchParams.get('from_month'), MONTH_FORMAT)
      : moment().startOf('month')
  );
  const [toMonth, setToMonth] = useState<any>(
    searchParams.get('to_month')
      ? moment(searchParams.get('to_month'), MONTH_FORMAT)
      : moment()
  );
  const [ordersPage, setOrdersPage] = useState(
    parseInt(searchParams.get('page') || '1', 10)
  );
  const [hasSearched, setHasSearched] = useState(false);

  const {
    data: reconciliationData,
    isLoading: isLoadingReconciliation,
    load: loadReconciliation,
  } = useFetchApiGet(apiReconciliation, { resourceName: 'data' });

  const {
    data: ordersResponse,
    isLoading: isLoadingOrders,
    load: loadOrders,
  } = useFetchApiGet(apiStockOrderList);

  const buildReconciliationParams = () => {
    const params: Record<string, string> = {};
    if (orderSource) params.order_source = orderSource;
    if (accountSource) params.account_source = accountSource;
    if (brokerName) params.broker_name = brokerName;
    if (fromMonth) params.from_month = fromMonth.format(MONTH_FORMAT);
    if (toMonth) params.to_month = toMonth.format(MONTH_FORMAT);
    return params;
  };

  const buildOrdersParams = (page = 1) => {
    const params: Record<string, string> = {
      page: String(page),
      page_size: String(PAGE_SIZE),
      ordering: '-order_date',
    };
    if (orderSource) params.order_source = orderSource;
    if (accountSource) params.account_source = accountSource;
    if (brokerName) params.broker_name = brokerName;
    if (fromMonth) params.from_month = fromMonth.format(MONTH_FORMAT);
    if (toMonth) params.to_month = toMonth.format(MONTH_FORMAT);
    return params;
  };

  const syncUrlParams = (page: number) => {
    const next: Record<string, string> = {};
    // Always written (even '' = "Tất cả") so a reload doesn't fall back to the 'stag' default.
    next.order_source = orderSource;
    next.account_source = accountSource;
    if (brokerName) next.broker_name = brokerName;
    if (fromMonth) next.from_month = fromMonth.format(MONTH_FORMAT);
    if (toMonth) next.to_month = toMonth.format(MONTH_FORMAT);
    if (page > 1) next.page = String(page);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (searchParams.get('from_month')) {
      setHasSearched(true);
      loadReconciliation(buildReconciliationParams());
      loadOrders(buildOrdersParams(ordersPage));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = () => {
    const page = 1;
    setOrdersPage(page);
    setHasSearched(true);
    syncUrlParams(page);
    loadReconciliation(buildReconciliationParams());
    loadOrders(buildOrdersParams(page));
  };

  const handleOrdersPageChange = (page: number) => {
    setOrdersPage(page);
    syncUrlParams(page);
    loadOrders(buildOrdersParams(page));
  };

  const handleTabChange = (key: string) => {
    const base = '/accounting/stock-order-reconciliation';
    const qs = searchParams.toString() ? `?${searchParams.toString()}` : '';
    navigate(key === TAB_SUMMARY ? `${base}/summary${qs}` : `${base}${qs}`);
  };

  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    const params = buildReconciliationParams();
    const query = new URLSearchParams(params).toString();
    const url = `${API_BASE_URL}/api/admin/stock-orders/reconciliation/export${
      query ? `?${query}` : ''
    }`;

    const { token } = getAccessToken();
    setIsExporting(true);
    try {
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          ...staffSessionHeaders(),
        },
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `stock_order_reconciliation_${moment().format(
        'YYYYMMDD_HHmmss'
      )}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    } catch {
      message.error('Xuất Excel thất bại. Vui lòng thử lại.');
    } finally {
      setIsExporting(false);
    }
  };

  const reconciliationRows: any[] = Array.isArray(reconciliationData)
    ? reconciliationData
    : [];
  const orders: any[] = ordersResponse?.data ?? [];
  const paging = ordersResponse?.paging ?? null;

  const filterBar = (
    <Space wrap style={{ marginBottom: 16 }}>
      <div>
        <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>
          Nguồn tài khoản
        </div>
        <Select
          style={{ width: 130 }}
          value={accountSource}
          onChange={setAccountSource}
          options={[
            { value: '', label: 'Tất cả' },
            { value: 'stag', label: 'Stag' },
            { value: 'nhsv', label: 'NHSV' },
          ]}
        />
      </div>

      <div>
        <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>
          Nguồn lệnh
        </div>
        <Select
          style={{ width: 130 }}
          value={orderSource}
          onChange={setOrderSource}
          options={[
            { value: '', label: 'Tất cả' },
            { value: 'stag', label: 'Stag' },
            { value: 'nhsv', label: 'NHSV' },
          ]}
        />
      </div>

      <div>
        <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>
          Nhà môi giới
        </div>
        <Input
          style={{ width: 160 }}
          value={brokerName}
          onChange={e => setBrokerName(e.target.value)}
          onPressEnter={handleSearch}
          placeholder="Tên môi giới"
          allowClear
        />
      </div>

      <div>
        <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>
          Từ tháng
        </div>
        <MonthPicker
          value={fromMonth}
          onChange={setFromMonth}
          format="MM/YYYY"
          placeholder="Từ tháng"
          style={{ width: 140 }}
        />
      </div>

      <div>
        <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>
          Đến tháng
        </div>
        <MonthPicker
          value={toMonth}
          onChange={setToMonth}
          format="MM/YYYY"
          placeholder="Đến tháng"
          style={{ width: 140 }}
        />
      </div>

      <div style={{ marginTop: 20 }}>
        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={handleSearch}
          loading={isLoadingReconciliation || isLoadingOrders}
        >
          Tìm kiếm
        </Button>
      </div>

      <div style={{ marginTop: 20 }}>
        <Button
          icon={<DownloadOutlined />}
          onClick={handleExport}
          loading={isExporting}
          disabled={!hasSearched}
        >
          Xuất Excel
        </Button>
      </div>
    </Space>
  );

  const tabItems = [
    {
      key: TAB_DETAIL,
      label: `Chi tiết lệnh${paging ? ` (${paging.count})` : ''}`,
      children: (
        <Table
          dataSource={orders}
          columns={orderDetailColumns}
          rowKey="id"
          size="small"
          scroll={{ x: 1670 }}
          loading={isLoadingOrders}
          locale={{
            emptyText: hasSearched
              ? 'Không có lệnh nào'
              : 'Chọn bộ lọc và nhấn Tìm kiếm',
          }}
          pagination={{
            current: ordersPage,
            pageSize: PAGE_SIZE,
            total: paging?.count ?? 0,
            showSizeChanger: false,
            showTotal: total => `${total} lệnh`,
            onChange: handleOrdersPageChange,
          }}
        />
      ),
    },
    {
      key: TAB_SUMMARY,
      label: `Tổng hợp${
        reconciliationRows.length ? ` (${reconciliationRows.length})` : ''
      }`,
      children: (
        <Table
          dataSource={reconciliationRows}
          columns={summaryColumns}
          rowKey={(record: any) => `${record.month}_${record.symbol}`}
          pagination={false}
          size="small"
          scroll={{ x: 1100 }}
          locale={{
            emptyText: hasSearched
              ? 'Không có dữ liệu'
              : 'Chọn bộ lọc và nhấn Tìm kiếm',
          }}
          loading={isLoadingReconciliation}
        />
      ),
    },
  ];

  const totalOrders = reconciliationRows.reduce(
    (s, r) => s + (r.total_orders ?? 0),
    0
  );
  const totalBuy = reconciliationRows.reduce(
    (s, r) => s + (r.total_buy_matched_value ?? 0),
    0
  );
  const totalSell = reconciliationRows.reduce(
    (s, r) => s + (r.total_sell_matched_value ?? 0),
    0
  );
  const totalGD = totalBuy + totalSell;

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>Đối soát lệnh cổ phiếu</Title>
      {filterBar}
      {reconciliationRows.length > 0 && (
        <div
          style={{
            display: 'flex',
            gap: 32,
            marginBottom: 16,
            padding: '12px 16px',
            background: '#f5f5f5',
            borderRadius: 6,
          }}
        >
          <div>
            <div style={{ fontSize: 12, color: '#666' }}>Tổng số lệnh</div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>
              {totalOrders.toLocaleString('vi-VN')}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#666' }}>Tổng giá trị GD</div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>
              {totalGD.toLocaleString('vi-VN')} ₫
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#666' }}>Tổng mua</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#389e0d' }}>
              {totalBuy.toLocaleString('vi-VN')} ₫
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#666' }}>Tổng bán</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#cf1322' }}>
              {totalSell.toLocaleString('vi-VN')} ₫
            </div>
          </div>
        </div>
      )}
      <Tabs activeKey={activeTab} onChange={handleTabChange} items={tabItems} />
    </div>
  );
}
